"use client";

import { type StorefrontUrls } from "@/app/utilities/storefront-urls";
import { Breadcrumbs } from "@/composites/navigation";
import {
	PlpFacetRail,
	PlpHero,
	PlpPagination,
	PlpPopularFilters,
	PlpProductGrid,
	PlpResultsToolbar
} from "@/composites/product-listing";
import { type SuperCategory } from "@/domain/product-listing";
import {
	Col as DSCol,
	Container,
	Grid,
	type ColProps
} from "@pamperedchef/design-system";
import { type FC, type ReactNode } from "react";
import { buildPlpBreadcrumbItems } from "./helpers/build-plp-breadcrumb-items";

const Col = DSCol as FC<ColProps & { children?: ReactNode }>;

// Relative-href default for stories / tests that don't pass `urls`. Real
// storefront URLs are resolved server-side via `getStorefrontUrls()` in the
// route and threaded down — never resolved inside this component.
const DEFAULT_HOME_URL = "/";

export interface ProductListingTemplateProps {
	category: SuperCategory;
	urls?: Pick<StorefrontUrls, "home">;
}

/**
 * Shell for the PLP. Composes the page layout from STUB placeholder composites
 * (hero, popular filters, filter rail, results grid + card, sort toolbar,
 * pagination) — each is a placeholder that a follow-up slice replaces with the
 * real, data-driven component. The `Container`/`Grid`/`Col` wrapper mirrors the
 * PDP so width and gutters match. Path-only (no `searchParams`/cookies), so the
 * shop route stays ISR/full-route cacheable like the PDP.
 */
export function ProductListingTemplate({
	category,
	urls
}: ProductListingTemplateProps): JSX.Element {
	return (
		<article
			data-template="ProductListingTemplate"
			data-category={category.slug}
		>
			<Container>
				<Grid columns={12} gap="lg">
					<Col size={12}>
						<Breadcrumbs
							items={buildPlpBreadcrumbItems(category, {
								homeUrl: urls?.home ?? DEFAULT_HOME_URL
							})}
						/>
					</Col>

					<Col size={12}>
						<PlpHero title={category.name} />
					</Col>

					<Col size={12}>
						<PlpPopularFilters />
					</Col>

					<Col size={12} md={3}>
						<PlpFacetRail />
					</Col>

					<Col size={12} md={9}>
						<PlpResultsToolbar />
						<PlpProductGrid />
						<PlpPagination />
					</Col>
				</Grid>
			</Container>
		</article>
	);
}
